import { getUser } from '../../services/UserService';
import { REPORT_DATE_RANGE_PRESETS } from '../workflow/ReportDateRangePicker';

/**
 * Per-user, per-customization viewer preferences in localStorage (e.g. the last
 * date range picked on a saved report/export/grid). Keyed by user ID so shared
 * machines don't leak one user's choices into another's session.
 *
 * stateKey is the customization's stable state key ("cg_{customizationID}") — the
 * same key the saved grid uses for its GridSettings row and quick-filter scope.
 */

const safeString = (v) => (v ?? '').toString();

const storageKeyFor = (stateKey) => `insyte_customization_prefs_${getUser()?.ID ?? 0}_${safeString(stateKey)}`;

export const getCustomizationPrefs = (stateKey) => {
  if (!stateKey) return null;
  try {
    const raw = window.localStorage.getItem(storageKeyFor(stateKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

export const patchCustomizationPrefs = (stateKey, patch) => {
  if (!stateKey || !patch || typeof patch !== 'object') return;
  try {
    const next = { ...(getCustomizationPrefs(stateKey) ?? {}), ...patch };
    window.localStorage.setItem(storageKeyFor(stateKey), JSON.stringify(next));
  } catch {
    // Storage full/blocked: preferences are a convenience, never an error.
  }
};

const isKnownPreset = (preset) => REPORT_DATE_RANGE_PRESETS.some((p) => p.id === preset);

/**
 * The initial date-range selection for a preview, by precedence:
 * 1. the viewer's own remembered selection (localStorage, incl. their custom dates),
 * 2. the author's default baked into the definition (sessionOptions.defaultDateRange —
 *    preset only, never dates: "Custom" seeds an empty custom range),
 * 3. the historical default, current month.
 */
export const resolveInitialDateRange = (stateKey, sessionOptions) => {
  const saved = getCustomizationPrefs(stateKey)?.dateRange;
  if (saved && isKnownPreset(safeString(saved.preset))) {
    return {
      preset: safeString(saved.preset),
      customFrom: saved.customFrom ? new Date(saved.customFrom) : null,
      customTo: saved.customTo ? new Date(saved.customTo) : null,
    };
  }
  const authorDefault = safeString(sessionOptions?.defaultDateRange).trim();
  if (isKnownPreset(authorDefault)) {
    return { preset: authorDefault, customFrom: null, customTo: null };
  }
  return { preset: 'currentMonth', customFrom: null, customTo: null };
};
